import { DialogComponent } from '@theme/dialog';

const DAY_MS = 24 * 60 * 60 * 1000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class SignupPopupComponent extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'formView', 'successView', 'successHeading', 'status', 'submitButton'];

  #triggerController;
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#triggerController = new AbortController();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect, { signal: this.#triggerController.signal });
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect, { signal: this.#triggerController.signal });
      return;
    }

    if (!this.#isEligible()) return;

    this.addEventListener('dialog:close', this.#handleClose);

    const { signal } = this.#triggerController;

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.documentElement.addEventListener('mouseleave', this.#handleExitIntent, { signal });
    } else {
      window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#triggerController.abort();
    this.removeEventListener('dialog:close', this.#handleClose);
  }

  get #storageKey() {
    return `nomoSignupPopup:${this.dataset.listId}`;
  }

  #readState() {
    try {
      return JSON.parse(localStorage.getItem(this.#storageKey) ?? 'null');
    } catch {
      return null;
    }
  }

  #writeState(state) {
    try {
      localStorage.setItem(this.#storageKey, JSON.stringify(state));
    } catch {}
  }

  #isEligible() {
    const excluded = (this.dataset.excludedPaths ?? '')
      .split(',')
      .map((path) => path.trim())
      .filter(Boolean);
    if (excluded.some((path) => window.location.pathname.includes(path))) return false;

    const state = this.#readState();
    if (!state) return true;
    if (state.subscribed) return false;

    const days = Number(this.dataset.periodDays) || 5;
    return Date.now() - (state.suppressedAt ?? 0) > days * DAY_MS;
  }

  #open() {
    this.#triggerController.abort();
    if (!this.#isEligible()) return;

    this.#writeState({ suppressedAt: Date.now() });
    this.showDialog();
  }

  #handleExitIntent = (event) => {
    if (event.clientY > 0 || event.relatedTarget) return;
    this.#open();
  };

  #handleScroll = () => {
    const depth = Number(this.dataset.scrollDepth) || 50;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    if ((window.scrollY / scrollable) * 100 >= depth) this.#open();
  };

  #handleClose = () => {
    const state = this.#readState() ?? {};
    this.#writeState({ ...state, suppressedAt: Date.now() });
  };

  #handleEditorSelect = (event) => {
    if (event.detail.sectionId === this.dataset.sectionId) this.showDialog();
  };

  #handleEditorDeselect = (event) => {
    if (event.detail.sectionId === this.dataset.sectionId) this.closeDialog();
  };

  #setError(inputs, errorId, message) {
    for (const input of inputs) {
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
    const error = this.querySelector(`#${errorId}`);
    error.textContent = message;
    error.hidden = !message;
    return message ? inputs[0] : null;
  }

  #validate(errors) {
    const { elements } = this.refs.form;
    const firstName = elements.namedItem('first_name');
    const email = elements.namedItem('email');
    const caregiver = elements.namedItem('caregiver');
    const emailValue = email.value.trim();
    const emailMessage = !emailValue ? errors.errorEmailRequired : EMAIL_PATTERN.test(emailValue) ? '' : errors.errorEmailInvalid;
    const { sectionId } = this.dataset;

    return [
      this.#setError([firstName], `SignupPopupFirstNameError-${sectionId}`, firstName.value.trim() ? '' : errors.errorFirstName),
      this.#setError([email], `SignupPopupEmailError-${sectionId}`, emailMessage),
      this.#setError(Array.from(caregiver), `SignupPopupCaregiverError-${sectionId}`, caregiver.value ? '' : errors.errorCaregiver),
    ].filter(Boolean);
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    const { form, status, submitButton } = this.refs;
    status.textContent = '';

    const invalid = this.#validate(this.dataset);
    if (invalid.length) {
      invalid[0].focus();
      return;
    }

    this.#submitting = true;
    submitButton.setAttribute('aria-disabled', 'true');

    const data = new FormData(form);
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.dataset.source,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: String(data.get('email')).trim(),
                first_name: String(data.get('first_name')).trim(),
                properties: { [this.dataset.caregiverProperty]: data.get('caregiver') },
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: this.dataset.listId } } },
      },
    };

    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.siteId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: this.dataset.revision },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(String(response.status));

      this.#writeState({ subscribed: true, suppressedAt: Date.now() });
      this.#showSuccess();
    } catch {
      status.textContent = this.dataset.errorSubmit;
    } finally {
      this.#submitting = false;
      submitButton.removeAttribute('aria-disabled');
    }
  };

  #showSuccess() {
    const { formView, successView, successHeading } = this.refs;
    formView.hidden = true;
    successView.hidden = false;
    successHeading.focus();
  }
}

if (!customElements.get('signup-popup-component')) {
  customElements.define('signup-popup-component', SignupPopupComponent);
}
